import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Stack,
  Typography,
  IconButton,
  Chip,
  Box,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Tabs } from 'new/elements/Tabs/Tabs';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  FolderOff as FolderMinusIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { ReactComponent as ToolIcon } from 'new/components/icons/tool.svg';
import { IndividualTool, UIStatus } from '../../types/ui';
import { ToolChangelog, ToolChangeType, ToolTriggerType } from '../../types/toolContract';
import { useTools } from '../../hooks/useTools';
import styles from './IndividualToolDetailsDrawer.module.scss';

interface IndividualToolDetailsDrawerProps {
  tool: IndividualTool | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (tool: IndividualTool) => void;
  onTest?: (toolId: string) => void;
  onDelete?: (toolId: string) => void;
  onReconcile?: (toolId: string) => void;
  onRemoveFromCollection?: (toolId: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function getStatusColor(status: UIStatus): 'success' | 'warning' | 'default' {
  switch (status) {
    case UIStatus.Active:
      return 'success';
    case UIStatus.Paused:
      return 'warning';
    case UIStatus.Init:
    default:
      return 'default';
  }
}

function getChangeTypeLabel(changeType: ToolChangeType): string {
  switch (changeType) {
    case ToolChangeType.Created:
      return 'Created';
    case ToolChangeType.DefinitionUpdated:
      return 'Definition Updated';
    case ToolChangeType.MetadataUpdated:
      return 'Metadata Updated';
    case ToolChangeType.StatusChanged:
      return 'Status Changed';
    case ToolChangeType.Deleted:
      return 'Deleted';
    default:
      return changeType;
  }
}

function getChangeTypeColor(
  changeType: ToolChangeType,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (changeType) {
    case ToolChangeType.Created:
      return 'success';
    case ToolChangeType.DefinitionUpdated:
      return 'info';
    case ToolChangeType.MetadataUpdated:
      return 'default';
    case ToolChangeType.StatusChanged:
      return 'warning';
    case ToolChangeType.Deleted:
      return 'error';
    default:
      return 'default';
  }
}

function getTriggerTypeLabel(triggerType: ToolTriggerType): string {
  switch (triggerType) {
    case ToolTriggerType.UserAction:
      return 'User Action';
    case ToolTriggerType.NexsetSync:
      return 'Nexset Sync';
    case ToolTriggerType.ApiCall:
      return 'API Call';
    case ToolTriggerType.System:
      return 'System';
    default:
      return triggerType;
  }
}

const IndividualToolDetailsDrawer: React.FC<IndividualToolDetailsDrawerProps> = ({
  tool,
  open,
  onClose,
  onEdit,
  onTest,
  onDelete,
  onReconcile,
  onRemoveFromCollection,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [changelogs, setChangelogs] = useState<ToolChangelog[]>([]);
  const [changelogsLoading, setChangelogsLoading] = useState(false);

  const { fetchChangelogs } = useTools({ autoFetch: false });

  const loadChangelogs = useCallback(
    async (toolId: string) => {
      setChangelogsLoading(true);
      try {
        const result = await fetchChangelogs(toolId, { limit: 50 });
        const items = result?.result?.items || result?.items || result || [];
        setChangelogs(Array.isArray(items) ? items : []);
      } catch {
        setChangelogs([]);
      } finally {
        setChangelogsLoading(false);
      }
    },
    [fetchChangelogs],
  );

  useEffect(() => {
    if (open && tool && tabValue === 3) {
      loadChangelogs(tool.id);
    }
  }, [open, tool, tabValue, loadChangelogs]);

  // Reset changelogs when drawer closes
  useEffect(() => {
    if (!open) {
      setChangelogs([]);
      setTabValue(0);
    }
  }, [open]);

  if (!tool) return null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 520, maxWidth: '100%' },
      }}
    >
      <div className={styles.drawerContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <div className={styles.toolIcon}>
                <ToolIcon />
              </div>
              <div>
                <Typography variant="h6">{tool.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {tool.nexsetName}
                </Typography>
              </div>
            </Stack>
            <Stack direction="row" spacing={1} mt={1.5}>
              <Chip
                label={tool.statusLabel || tool.uiStatus}
                size="small"
                color={getStatusColor(tool.uiStatus)}
              />
              {tool.category && <Chip label={tool.category} size="small" variant="outlined" />}
            </Stack>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Actions */}
        <Stack direction="row" spacing={1} px={3} pt={2}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => onEdit?.(tool)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayIcon />}
            onClick={() => onTest?.(tool.id)}
          >
            Test
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => onReconcile?.(tool.id)}
          >
            Reconcile
          </Button>
        </Stack>

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue as number)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
          tabs={[
            {
              icon: <InfoIcon fontSize="small" />,
              iconPosition: 'start',
              label: 'Overview',
              value: 0,
            },
            {
              icon: <SpeedIcon fontSize="small" />,
              iconPosition: 'start',
              label: 'Semantics',
              value: 1,
            },
            {
              icon: <SecurityIcon fontSize="small" />,
              iconPosition: 'start',
              label: 'Auth & Governance',
              value: 2,
            },
            {
              icon: <HistoryIcon fontSize="small" />,
              iconPosition: 'start',
              label: 'Changelog',
              value: 3,
            },
          ]}
        />

        <div className={styles.tabContent}>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              {tool.description && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    DESCRIPTION
                  </Typography>
                  <Typography variant="body2">{tool.description}</Typography>
                </div>
              )}

              <div className={styles.field}>
                <Typography variant="caption" color="text.secondary">
                  TOOL KEY
                </Typography>
                <code className={styles.code}>{tool.toolKey}</code>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    NEXSET ID
                  </Typography>
                  <Typography variant="body2">{tool.nexsetId}</Typography>
                </div>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    CREATED
                  </Typography>
                  <Typography variant="body2">{formatDate(tool.createdAt)}</Typography>
                </div>
              </div>

              {tool.tags && tool.tags.length > 0 && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    TAGS
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {tool.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Stack>
                </div>
              )}
            </Stack>
          </TabPanel>

          {/* Semantics Tab */}
          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    OPERATION TYPE
                  </Typography>
                  <Chip
                    label={tool.semantics.operation}
                    size="small"
                    color={tool.semantics.operation === 'read' ? 'info' : 'warning'}
                  />
                </div>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    SIDE EFFECTS
                  </Typography>
                  <Chip label={tool.semantics.sideEffects} size="small" />
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    RESULT SHAPE
                  </Typography>
                  <Typography variant="body2">{tool.semantics.resultShape}</Typography>
                </div>
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    EXECUTION MODE
                  </Typography>
                  <Typography variant="body2">{tool.execution.mode}</Typography>
                </div>
              </div>

              {tool.execution.timeoutMs && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    TIMEOUT
                  </Typography>
                  <Typography variant="body2">{tool.execution.timeoutMs}ms</Typography>
                </div>
              )}

              {tool.semantics.readSemantics && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    READ SEMANTICS
                  </Typography>
                  <Typography variant="body2">{tool.semantics.readSemantics}</Typography>
                </div>
              )}

              {tool.semantics.rateLimitHint && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    RATE LIMIT
                  </Typography>
                  <Typography variant="body2">
                    {tool.semantics.rateLimitHint.requestsPerMinute} req/min
                    {tool.semantics.rateLimitHint.burst &&
                      ` (burst: ${tool.semantics.rateLimitHint.burst})`}
                  </Typography>
                </div>
              )}
            </Stack>
          </TabPanel>

          {/* Auth & Governance Tab */}
          <TabPanel value={tabValue} index={2}>
            <Stack spacing={3}>
              <Typography variant="subtitle2">Authentication</Typography>
              <div className={styles.field}>
                <Typography variant="caption" color="text.secondary">
                  CALLER IDENTITY TYPE
                </Typography>
                <Chip label={tool.auth.callerIdentityType} size="small" />
              </div>

              {tool.auth.requiredScopes && tool.auth.requiredScopes.length > 0 && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    REQUIRED SCOPES
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {tool.auth.requiredScopes.map((scope) => (
                      <Chip key={scope} label={scope} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </div>
              )}

              <Divider />

              <Typography variant="subtitle2">Governance</Typography>
              <div className={styles.field}>
                <Typography variant="caption" color="text.secondary">
                  DECISION MODEL
                </Typography>
                <Typography variant="body2">{tool.governance.decisionModel}</Typography>
              </div>

              {tool.governance.dataClassification && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    DATA CLASSIFICATION
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={tool.governance.dataClassification.level}
                      size="small"
                      color="warning"
                    />
                    {tool.governance.dataClassification.labels.map((label) => (
                      <Chip key={label} label={label} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </div>
              )}

              {tool.governance.audit && (
                <div className={styles.field}>
                  <Typography variant="caption" color="text.secondary">
                    AUDIT
                  </Typography>
                  <Typography variant="body2">
                    Log receipts: {tool.governance.audit.logReceipts ? 'Yes' : 'No'}, Retain:{' '}
                    {tool.governance.audit.retainDays} days
                  </Typography>
                </div>
              )}
            </Stack>
          </TabPanel>

          {/* Changelog Tab */}
          <TabPanel value={tabValue} index={3}>
            {changelogsLoading ? (
              <div className={styles.emptyState}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Loading changelog...
                </Typography>
              </div>
            ) : changelogs.length > 0 ? (
              <Stack spacing={2}>
                {changelogs.map((changelog) => (
                  <div key={changelog.id} className={styles.versionItem}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          label={getChangeTypeLabel(changelog.change_type)}
                          size="small"
                          color={getChangeTypeColor(changelog.change_type)}
                        />
                        <Chip
                          label={getTriggerTypeLabel(changelog.trigger_type)}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(changelog.created_at)}
                      </Typography>
                    </Stack>
                    {changelog.field_changes && Object.keys(changelog.field_changes).length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Changed fields: {Object.keys(changelog.field_changes).join(', ')}
                      </Typography>
                    )}
                  </div>
                ))}
              </Stack>
            ) : (
              <div className={styles.emptyState}>
                <HistoryIcon sx={{ opacity: 0.5, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No changelog entries available
                </Typography>
              </div>
            )}
          </TabPanel>
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>
          {onRemoveFromCollection && (
            <Button
              size="small"
              startIcon={<FolderMinusIcon />}
              onClick={() => onRemoveFromCollection(tool.id)}
            >
              Remove from Package
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete?.(tool.id)}
          >
            Delete Tool
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default IndividualToolDetailsDrawer;
